import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { http } from "@/lib/api";
import { resolveSiteSlug } from "@/config/siteConfig";
import NoIndex from "@/components/NoIndex";
import { Award, Flower } from "lucide-react";
import ChangePasswordCard from "@/components/ChangePasswordCard";
import FamilyHomeCard from "@/components/FamilyHomeCard";
import MyPostsFeed from "@/components/MyPostsFeed";
import ShareLeaderboard from "@/components/ShareLeaderboard";
import DigestPreferences from "@/components/DigestPreferences";
import SubscriptionCard from "@/components/SubscriptionCard";
import AdminInboxCard from "@/components/AdminInboxCard";
import MessagesCard from "@/components/MessagesCard";
import MyEventsCard from "@/components/MyEventsCard";
import MyMarketplaceCard from "@/components/MyMarketplaceCard";
import DonateInvitation from "@/components/DonateInvitation";
import ActiveUsersWidget from "@/components/ActiveUsersWidget";

const LEVELS = [
  ["Helper", 0], ["Friend", 50], ["Supporter", 150],
  ["Community Builder", 400], ["Impact Leader", 1000],
  ["Joy Ambassador", 2500], ["Global Changemaker", 6000],
];
function levelOf(p) {
  let l = LEVELS[0][0]; for (const [n, t] of LEVELS) if (p >= t) l = n; return l;
}
function nextThreshold(p) {
  for (const [n, t] of LEVELS) if (t > p) return { n, t };
  return null;
}

export default function Dashboard() {
  const { user, loading, refresh } = useAuth();
  const [detail, setDetail] = useState(null);
  const [myCauses, setMyCauses] = useState([]);
  const [myMemorials, setMyMemorials] = useState([]);

  useEffect(() => { if (user) http.get("/auth/me").then((r) => setDetail(r.data)); }, [user]);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { if (user) http.get("/causes/mine").then((r) => setMyCauses(r.data)).catch(() => {}); }, [user]);
  useEffect(() => { if (user) http.get("/memorials/mine/followed").then((r) => setMyMemorials(r.data.items || [])).catch(() => {}); }, [user]);

  if (loading) return <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const pts = user.social_capital ?? 0;
  const level = levelOf(pts);
  const next = nextThreshold(pts);
  const prog = next ? Math.min(100, Math.round((pts / next.t) * 100)) : 100;

  return (
    <div data-testid="dashboard-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
      {/* Dashboards are per-user content — never surface in search */}
      <NoIndex />
      {resolveSiteSlug() === "indianfamilytree" && <FamilyHomeCard user={user} />}
      {detail?.warnings?.length > 0 && (
        <div data-testid="user-warnings" className="mb-6 rounded-2xl border-l-4 p-5" style={{ borderColor: "#B23A3A", background: "rgba(178,58,58,0.05)" }}>
          <div className="overline" style={{ color: "#B23A3A" }}>NOTICE FROM SOCIAL PULSE</div>
          <p className="mt-2 text-sm" style={{ color: "var(--wofy-text)" }}>
            You have received {detail.warnings.length} warning{detail.warnings.length > 1 ? "s" : ""} from our moderation team. Please review below.
          </p>
          <ul className="mt-3 space-y-2">
            {detail.warnings.map((w) => (
              <li key={w.id} className="text-sm border-l-2 pl-3" style={{ borderColor: "#B23A3A" }}>
                <div className="text-[11px] font-mono uppercase" style={{ color: "var(--wofy-text-soft)" }}>
                  {new Date(w.created_at).toLocaleString()} · {w.admin_name}
                </div>
                <div className="mt-1">{w.comment}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {["suspended", "blocked"].includes(user.status) && (
        <div className="mb-6 rounded-2xl border-l-4 p-5" style={{ borderColor: "#B25D38", background: "rgba(178,93,56,0.06)" }}>
          <div className="overline" style={{ color: "#B25D38" }}>ACCOUNT {(user.status || "").toUpperCase()}</div>
          <p className="mt-2 text-sm">
            Your account is currently <b>{user.status}</b>. You can still browse, but posting new content is disabled. <Link to="/contact" className="underline">Contact us</Link> if you'd like to appeal.
          </p>
        </div>
      )}
      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 wofy-card">
          <div className="overline">WELCOME</div>
          <h1 className="font-display text-4xl md:text-5xl mt-2">{user.name}.</h1>
          <p className="mt-3 text-sm" style={{ color: "var(--wofy-text-soft)" }}>{(user.role || "member").toUpperCase()} · {user.city || "—"}, {user.country || "—"}</p>

          <div className="mt-10 grid grid-cols-3 gap-6">
            <div>
              <div className="overline">SOCIAL CAPITAL</div>
              <div className="font-display text-4xl mt-1 font-mono">{pts}</div>
            </div>
            <div>
              <div className="overline">LEVEL</div>
              <div className="font-display text-3xl mt-1">{level}</div>
            </div>
            <div>
              <div className="overline">HOURS SERVED</div>
              <div className="font-display text-4xl mt-1 font-mono">{user.hours_served ?? 0}</div>
            </div>
          </div>

          {next && (
            <div className="mt-8">
              <div className="flex justify-between text-xs font-mono mb-2"><span>Next: {next.n}</span><span>{pts} / {next.t}</span></div>
              <div className="h-1.5 w-full rounded-full" style={{ background: "var(--wofy-surface)" }}>
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${prog}%`, background: "var(--wofy-primary)" }} />
              </div>
            </div>
          )}
        </div>

        <aside className="lg:col-span-4 wofy-card">
          <div className="overline">ACHIEVEMENTS</div>
          <div className="mt-4 space-y-3">
            {(user.achievements ?? []).length === 0 && <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>Earn your first badge by volunteering or donating.</p>}
            {(user.achievements ?? []).map((a) => (
              <div key={a} className="flex items-center gap-3">
                <Award className="w-5 h-5" style={{ color: "var(--wofy-primary)" }} />
                <span className="text-sm capitalize">{a.replace(/-/g, " ")}</span>
              </div>
            ))}
          </div>
          <div className="wofy-divider my-6" />
          <div className="overline">VERIFICATION</div>
          <div className="mt-3 flex items-center gap-2 text-sm">
            <span className="wofy-tag uppercase">{user.verification_tier}</span>
            <span style={{ color: "var(--wofy-text-soft)" }}>Bronze = email verified</span>
          </div>
        </aside>
      </div>

      {/* IG-style personal posts feed */}
      <MyPostsFeed />

      {/* Active users strip */}
      <ActiveUsersWidget />

      {/* Most-shared this week */}
      <ShareLeaderboard days={7} limit={6} />

      {/* Personal messages from the Social Pulse admin team */}
      <AdminInboxCard />

      {/* Recent 1-on-1 conversations */}
      <MessagesCard />

      {/* Friendly ask — free users get a warm reminder to support the platform. */}
      <DonateInvitation />

      {/* Premium subscription — visible only for active subscribers */}
      <SubscriptionCard />

      {/* Your submissions — hidden unless you have some. */}
      <MyEventsCard />
      <MyMarketplaceCard />

      {/* Weekly share-digest email opt-in */}
      <DigestPreferences />

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <div className="wofy-card">
          <div className="overline mb-4">RECENT ACTIVITY</div>
          {(detail?.events ?? []).length === 0 && <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>No activity yet. <Link to="/opportunities" className="underline">Find something to do →</Link></p>}
          <ul className="space-y-3">
            {(detail?.events ?? []).slice(0, 10).map((e) => (
              <li key={e.id} className="flex justify-between text-sm">
                <span>{e.reason}</span>
                <span className="font-mono text-xs">+{e.points}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="wofy-card">
          <div className="overline mb-4">MY APPLICATIONS</div>
          {(detail?.applications ?? []).length === 0 && <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>No applications yet.</p>}
          <ul className="space-y-3">
            {(detail?.applications ?? []).map((a) => (
              <li key={a.id} className="flex justify-between text-sm">
                <span>{a.opportunity_title}</span>
                <span className="wofy-tag">{a.status}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="wofy-card mt-6" data-testid="my-causes-section">
        <div className="flex items-center justify-between">
          <div className="overline">MY CAUSES</div>
          <Link to="/submit-cause" className="wofy-btn-ghost text-xs">+ Submit new</Link>
        </div>
        {myCauses.length === 0 ? (
          <p className="mt-4 text-sm" style={{ color: "var(--wofy-text-soft)" }}>You haven't submitted any causes yet. <Link to="/submit-cause" className="underline">Submit your first cause →</Link></p>
        ) : (
          <ul className="mt-4 space-y-3">
            {myCauses.map((c) => (
              <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 border-b pb-2" style={{ borderColor: "var(--wofy-border)" }}>
                <div>
                  <div className="text-sm font-medium">{c.title}</div>
                  <div className="text-[11px] font-mono" style={{ color: "var(--wofy-text-soft)" }}>{new Date(c.created_at).toLocaleDateString()} · {c.category}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="wofy-tag text-[10px] uppercase">{c.status.replace("_"," ")}</span>
                  {(c.status === "approved" || c.status === "published") && <Link to={`/causes/${c.id}`} className="text-xs underline">View public</Link>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      <ChangePasswordCard hasPassword={Boolean(user.email)} />

      {/* Followed memorials */}
      <div className="wofy-card mt-6" data-testid="dash-followed-memorials">
        <div className="flex items-center gap-2 mb-4">
          <Flower className="w-5 h-5" style={{ color: "var(--wofy-primary)" }} />
          <h3 className="font-display text-2xl">Memorials you follow</h3>
        </div>
        {myMemorials.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>You're not following any memorials yet. Visit <Link to="/celebrate" className="underline">Celebrate a Life</Link> and click "Follow" on any memorial to receive anniversary reminders and updates.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {myMemorials.map((m) => (
              <Link key={m.id} to={`/celebrate/${m.slug || m.id}`} className="rounded-lg overflow-hidden hover:shadow-md border" style={{ borderColor: "var(--wofy-border)" }}>
                {m.portrait ? (
                  <img src={m.portrait} alt={m.full_name} className="w-full aspect-[4/3] object-cover" />
                ) : (
                  <div className="aspect-[4/3] flex items-center justify-center" style={{ background: "var(--wofy-surface)" }}><Flower className="w-8 h-8 opacity-30" /></div>
                )}
                <div className="p-3">
                  <div className="font-display text-lg leading-tight">{m.full_name}</div>
                  <div className="text-xs font-mono mt-1" style={{ color: "var(--wofy-text-soft)" }}>{[m.birth_date?.slice(0, 4), m.death_date?.slice(0, 4)].filter(Boolean).join("–")}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
